import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse } from '../types';
import { getStoredActiveTenantId } from './tenantStorage';

// ============================================
// API Client Configuration
// ============================================

const viteEnv = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env;

const API_BASE_URL = viteEnv?.VITE_API_URL || viteEnv?.VITE_API_BASE_URL || 'http://localhost:8080';
const CSRF_BOOTSTRAP_PATH = '/auth/web/csrf';
const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';

type ApiRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  _csrfRetry?: boolean;
};

interface CsrfBootstrapResponse {
  token: string;
  headerName?: string;
  parameterName?: string;
}

let csrfTokenCache: string | null = null;
let csrfBootstrapPromise: Promise<string | null> | null = null;

/**
 * Create axios instance with BFF cookie support
 * - withCredentials: true enables cookies for cross-origin requests
 * - This is critical for the BFF session cookie pattern
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // CRITICAL: Enables sending/receiving cookies
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Helps identify AJAX requests
  },
});

// ============================================
// Request Interceptor
// ============================================

apiClient.interceptors.request.use(
  async (config) => {
    attachTenantHeader(config);

    if (requiresConsumerWebCsrf(config)) {
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        config.headers = withHeader(config.headers, CSRF_HEADER_NAME, csrfToken);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================
// Response Interceptor
// ============================================

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as ApiRequestConfig;

    if (
      error.response?.status === 403 &&
      originalRequest &&
      !originalRequest._csrfRetry &&
      requiresConsumerWebCsrf(originalRequest)
    ) {
      originalRequest._csrfRetry = true;
      clearCsrfToken();
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        originalRequest.headers = withHeader(originalRequest.headers, CSRF_HEADER_NAME, csrfToken);
        return apiClient(originalRequest);
      }
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isNonRedirectAuthRequest(originalRequest.url)) {
        // Session expired during regular API call - redirect to login
        window.location.href = '/login?reason=session_expired';
      }

      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.error('Access denied:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// ============================================
// Helper Functions
// ============================================

function clearCsrfToken() {
  csrfTokenCache = null;
}

async function ensureCsrfToken(): Promise<string | null> {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = axios
      .get<CsrfBootstrapResponse>(CSRF_BOOTSTRAP_PATH, {
        baseURL: API_BASE_URL,
        withCredentials: true,
        timeout: 30000,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      .then((response) => {
        csrfTokenCache = response.data.token ?? null;
        return csrfTokenCache;
      })
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }

  return csrfBootstrapPromise;
}

function isStateChangingMethod(method?: string): boolean {
  const stateMethods = ['post', 'put', 'patch', 'delete'];
  return stateMethods.includes(method?.toLowerCase() || '');
}

function requiresConsumerWebCsrf(config: AxiosRequestConfig): boolean {
  if (!isStateChangingMethod(config.method)) {
    return false;
  }

  const url = config.url || '';
  if (url.includes(CSRF_BOOTSTRAP_PATH)) {
    return false;
  }

  return url.startsWith('/auth/web/') || url.startsWith('/api/v1/web/');
}

function isNonRedirectAuthRequest(url?: string): boolean {
  const requestUrl = url || '';
  return (
    requestUrl.includes('/auth/web/csrf') ||
    requestUrl.includes('/auth/web/start-login') ||
    requestUrl.includes('/auth/web/verify-otp') ||
    requestUrl.includes('/auth/web/me') ||
    requestUrl.includes('/auth/web/logout') ||
    requestUrl.includes('/auth/web/refresh') ||
    requestUrl.includes('/auth/admin/login') ||
    requestUrl.includes('/auth/admin/mfa') ||
    requestUrl.includes('/auth/admin/me') ||
    requestUrl.includes('/auth/admin/logout') ||
    requestUrl.includes('/api/v1/web/session')
  );
}

function attachTenantHeader(config: AxiosRequestConfig): void {
  const url = config.url || '';
  if (!url.startsWith('/api/v1/web/')) {
    return;
  }

  const tenantId = getStoredActiveTenantId();
  if (!tenantId) {
    return;
  }

  const nextHeaders =
    config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders((config.headers ?? {}) as Record<string, string>);
  nextHeaders.set('X-TARK-Tenant-Id', String(tenantId));
  config.headers = nextHeaders;
}

function withHeader(
  headers: AxiosRequestConfig['headers'],
  name: string,
  value: string
): AxiosHeaders {
  const nextHeaders =
    headers instanceof AxiosHeaders
      ? headers
      : new AxiosHeaders((headers ?? {}) as Record<string, string>);
  nextHeaders.set(name, value);
  return nextHeaders;
}

// ============================================
// Typed API Methods
// ============================================

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data.data;
}

export async function post<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function put<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function patch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await apiClient.patch<ApiResponse<T>>(url, data, config);
  return response.data.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data.data;
}

// ============================================
// Error Handling Utilities
// ============================================

export function isApiError(error: unknown): error is AxiosError<ApiError> {
  return axios.isAxiosError(error) && error.response?.data?.code !== undefined;
}

export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.response?.data?.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
